import type { FC } from "react";
import { useMemo } from "react";
import { Card, Progress } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Circle, ListItem, ListItemText, Text, XStack, YStack } from "tamagui";

import { api } from "~/utils/api";
import {
  OverviewCard,
  OverviewDiff,
  OverviewEmptyCard,
} from "./overview-common";

export const OverviewDangers: FC = () => {
  const { t } = useTranslation("overview");
  const [data] = api.overview.dangers.useSuspenseQuery();
  const empty = useMemo(() => data === null, [data]);

  return (
    <OverviewCard title={t("dangers.title")} subtitle={t("dangers.subtitle")}>
      <Card
        width={"100%"}
        variant="borderless"
        backgroundColor={"$warm-snow-500"}
        padding={"$3"}
      >
        <XStack gap={"$3"}>
          <DangerCard
            title={t("dangers.dangertoyourself")}
            value={data?.dangertoyourself.value}
            percent={data?.dangertoyourself.percent}
            diff={data?.dangertoyourself.dynamic}
            empty={empty}
          />
          <DangerCard
            title={t("dangers.dangertoother")}
            value={data?.dangertoother.value}
            percent={data?.dangertoother.percent}
            diff={data?.dangertoother.dynamic}
            empty={empty}
          />
        </XStack>
      </Card>
      <DangerSuggestions
        suggestions={t("dangers.suggestions", { returnObjects: true })}
        empty={empty}
      />
      <OverviewEmptyCard text={t("dangers.empty")} visible={empty} />
    </OverviewCard>
  );
};

interface DangerSuggestionsProps {
  suggestions: { title: string; desc: string }[];
  empty?: boolean;
}
const DangerSuggestions: FC<DangerSuggestionsProps> = ({
  empty = false,
  suggestions = [],
}) => {
  if (empty) return null;

  return (
    <YStack gap={"$2"}>
      {suggestions.map((suggestion, index) => (
        <SuggestionCard
          key={index}
          num={index + 1}
          title={suggestion.title}
          description={suggestion.desc}
        />
      ))}
    </YStack>
  );
};

interface DangerCardProps {
  title: string;
  value: number | undefined;
  diff: number | null | undefined;
  empty?: boolean;
  percent?: number | undefined;
}
const DangerCard: FC<DangerCardProps> = ({
  title,
  value = 0,
  diff = 0,
  percent = 0,
  empty = false,
}) => {
  const { titleColor, valueColor } = useMemo(() => {
    if (empty) return { titleColor: "$gray-200", valueColor: "$gray-200" };
    return { titleColor: "$color", valueColor: "$color" };
  }, [empty]);

  return (
    <Card
      flex={1}
      flexBasis={1}
      shadow="md"
      paddingVertical={"$3"}
      paddingHorizontal={"$4"}
    >
      <YStack gap={"$2"}>
        <XStack alignItems="center" gap={"$1"}>
          <XStack gap={"$2"} alignItems="center">
            <Text
              fontFamily={"$body"}
              fontSize={"$7"}
              fontWeight={"$6"}
              color={valueColor}
            >
              {value}
            </Text>
            <Text
              fontFamily={"$body"}
              fontSize={"$5"}
              fontWeight={"$5"}
              color={"$gray-600"}
            >
              |
            </Text>
            <Text
              fontFamily={"$body"}
              fontSize={"$5"}
              fontWeight={"$5"}
              color={"$gray-800"}
            >
              ({percent}%)
            </Text>
          </XStack>
          <OverviewDiff diff={diff} />
        </XStack>
        <Progress value={value} variant={"success"} height={"$0.5"} />
        <Text
          fontFamily={"$body"}
          fontWeight={500}
          fontSize={"$4"}
          maxWidth={"80%"}
          color={titleColor}
        >
          {title}
        </Text>
      </YStack>
    </Card>
  );
};

const SuggestionCard: FC<{
  num: number;
  title: string;
  description: string;
}> = ({ num, title, description }) => {
  return (
    <Card
      variant="borderless"
      borderRadius={"$2"}
      borderLeftWidth={"$1"}
      borderColor={"$darkpink-500"}
      backgroundColor={"$lightpink-100"}
      paddingHorizontal={"$6"}
      paddingVertical={"$3"}
    >
      <YStack>
        <Card.Title
          textAlign="left"
          fontSize={"$5"}
          lineHeight={"$5"}
          marginBottom={"$1"}
          fontWeight={"$6"}
        >
          {num}. {title}
        </Card.Title>
        <ListItem
          paddingLeft={"$1"}
          icon={
            <Circle
              size={4}
              marginTop={8}
              alignSelf="flex-start"
              backgroundColor="$color"
            />
          }
        >
          <ListItemText
            fontFamily={"$body"}
            lineHeight={"$3"}
            whiteSpace="pre-wrap"
          >
            {description}
          </ListItemText>
        </ListItem>
      </YStack>
    </Card>
  );
};
